import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { firstValueFrom } from 'rxjs';
import { UploadQueueDBService, UploadQueueItem } from '../database/upload-queue-db.service';
import { environment } from 'src/environments/environment';
import { BaseService } from '../alcance/services/_base.service';

@Injectable({
    providedIn: 'root'
})
export class UploadQueueService {
    private isSending = false;
    private retryBlockedUntil = 0;
    private memoryQueue = new Map<string, UploadQueueItem>();
    private inFlight = new Map<string, Promise<UploadQueueItem>>();

    constructor(
        private http: HttpClient,
        private uploadQueueDB: UploadQueueDBService,
    ){}

    async enqueue(upload: UploadQueueItem): Promise<UploadQueueItem>{
        const current = await this.uploadQueueDB.getByKey(upload.uuid).catch(() => null);
        const uploadToSave = {
            ...(current || {}),
            ...upload,
            status: upload.status || current?.status || 'pending',
            error: null,
        };
        const saved = await this.uploadQueueDB.put(uploadToSave).catch(() => uploadToSave);

        this.memoryQueue.set(saved.uuid, saved);

        return saved;
    }

    async enqueueMany(uploads: UploadQueueItem[]): Promise<UploadQueueItem[]>{
        const saved: UploadQueueItem[] = [];

        for(const upload of uploads){
            saved.push(await this.enqueue(upload));
        }

        return saved;
    }

    async enqueueAndSend(upload: UploadQueueItem): Promise<UploadQueueItem>{
        const saved = await this.enqueue(upload);

        if(BaseService.online !== false && Date.now() >= this.retryBlockedUntil){
            return this.sendUpload(saved);
        }

        return saved;
    }

    async uploadDirect(upload: UploadQueueItem): Promise<UploadQueueItem>{
        return this.postUpload({
            ...upload,
            status: 'uploading',
            error: undefined,
        });
    }

    async discard(uuid: string): Promise<void>{
        const uploadInFlight = this.inFlight.get(uuid);

        if(uploadInFlight){
            await uploadInFlight.catch(() => null);
        }

        const canCallServer = BaseService.online !== false;

        if(!Capacitor.isNativePlatform()){
            if(canCallServer){
                await this.deleteUpload(uuid);
            }
            return;
        }

        this.memoryQueue.delete(uuid);
        await this.uploadQueueDB.del(uuid).catch(() => null);

        if(canCallServer){
            await this.deleteUpload(uuid);
        }
    }

    async send(): Promise<UploadQueueItem[]>{
        if(this.isSending || Date.now() < this.retryBlockedUntil){
            return [];
        }

        this.isSending = true;
        const sent: UploadQueueItem[] = [];

        try{
            const pending = await this.uploadQueueDB.getAll({status: 'pending'}).catch(() => ({data: []}));
            const errors = await this.uploadQueueDB.getAll({status: 'error'}).catch(() => ({data: []}));
            const interrupted = await this.uploadQueueDB.getAll({status: 'uploading'}).catch(() => ({data: []}));
            const queue = this.mergeQueue([
                ...pending.data,
                ...errors.data,
                ...interrupted.data,
                ...this.memoryQueue.values(),
            ]);

            const persistedBlock = queue
                .filter((item) => item.last_status === 429 && !this.canRetry(item))
                .reduce((latest, item) => Math.max(latest, Date.parse(item.next_retry_at as string) || 0), 0);

            if(persistedBlock > Date.now()){
                this.retryBlockedUntil = persistedBlock;
                return sent;
            }

            for(const upload of queue.filter((item) => this.canRetry(item))){
                const result = await this.sendUpload(upload, true);
                sent.push(result);

                if(result.last_status === 429){
                    break;
                }
            }
        }finally{
            this.isSending = false;
        }

        return sent;
    }

    private async sendUpload(upload: UploadQueueItem, linkIfOwnerExists = false): Promise<UploadQueueItem>{
        upload.status = 'uploading';
        upload.error = undefined;
        upload.last_attempt_at = new Date().toISOString();
        this.memoryQueue.set(upload.uuid, upload);
        await this.uploadQueueDB.put(upload).catch(() => upload);

        return this.postUpload(upload, true, linkIfOwnerExists);
    }

    private async postUpload(upload: UploadQueueItem, persistQueue = false, linkIfOwnerExists = false): Promise<UploadQueueItem>{
        const currentRequest = this.inFlight.get(upload.uuid);
        if(currentRequest){
            return currentRequest;
        }

        const request = this.doPostUpload(upload, persistQueue, linkIfOwnerExists).finally(() => {
            this.inFlight.delete(upload.uuid);
        });

        this.inFlight.set(upload.uuid, request);

        return request;
    }

    private async doPostUpload(upload: UploadQueueItem, persistQueue = false, linkIfOwnerExists = false): Promise<UploadQueueItem>{
        try{
            const file = await this.readUploadFile(upload);
            const response: any = await firstValueFrom(
                this.http.post(environment.url + 'uploads', {
                    uuid: upload.uuid,
                    owner_uuid: upload.owner_uuid,
                    owner_table: upload.owner_table,
                    field: upload.field,
                    file: file.data,
                    filename: upload.filename || file.filename,
                    mime_type: upload.mime_type || file.mime_type,
                    metadata: upload.metadata,
                    link_if_owner_exists: linkIfOwnerExists,
                }, {
                    headers: {showLoader: 'false', showError: 'false'}
                })
            );

            // POST bem-sucedido significa que o arquivo ja esta no servidor.
            // O status "pending" do backend indica apenas que o owner ainda sera vinculado no save final.
            upload.status = 'done';
            upload.server_id = response?.id;
            upload.server_path = response?.path;
            upload.error = undefined;
            upload.attempts = 0;
            upload.last_status = undefined;
            upload.next_retry_at = undefined;

            if(persistQueue){
                await this.uploadQueueDB.put(upload).catch(() => upload);
                if(upload.status === 'done'){
                    this.memoryQueue.delete(upload.uuid);
                }else{
                    this.memoryQueue.set(upload.uuid, upload);
                }
            }
        }catch(error: any){
            upload.status = 'error';
            upload.error = this.errorMessage(error);
            upload.attempts = (upload.attempts || 0) + 1;
            upload.last_status = error?.status;

            const retryDelay = this.retryDelaySeconds(error, upload.attempts);
            upload.next_retry_at = new Date(Date.now() + retryDelay * 1000).toISOString();

            if(error?.status === 429){
                this.retryBlockedUntil = Date.now() + retryDelay * 1000;
            }

            if(persistQueue){
                await this.uploadQueueDB.put(upload).catch(() => upload);
                this.memoryQueue.set(upload.uuid, upload);
            }
        }

        return upload;
    }

    private canRetry(upload: UploadQueueItem): boolean{
        if(!upload.next_retry_at){
            return true;
        }

        const nextRetry = Date.parse(upload.next_retry_at);
        return Number.isNaN(nextRetry) || nextRetry <= Date.now();
    }

    private retryDelaySeconds(error: any, attempts: number): number{
        const retryAfter = Number(error?.headers?.get?.('Retry-After'));

        if(Number.isFinite(retryAfter) && retryAfter > 0){
            return Math.ceil(retryAfter);
        }

        const baseDelay = error?.status === 429 ? 30 : 5;
        return Math.min(baseDelay * Math.pow(2, Math.max(attempts - 1, 0)), 300);
    }

    private mergeQueue(uploads: UploadQueueItem[]): UploadQueueItem[]{
        const queue = new Map<string, UploadQueueItem>();

        uploads
            .filter(upload => upload.status === 'pending' || upload.status === 'error' || upload.status === 'uploading')
            .forEach(upload => queue.set(upload.uuid, upload));

        return [...queue.values()];
    }

    private async readUploadFile(upload: UploadQueueItem): Promise<{data: string; filename?: string; mime_type?: string}>{
        if(upload.path.indexOf('data:') === 0){
            return {
                data: upload.path,
                filename: upload.filename,
                mime_type: upload.mime_type || this.mimeFromDataUrl(upload.path),
            };
        }

        const contents = await Filesystem.readFile({
            path: upload.path,
            directory: upload.path.indexOf('file://') === 0 ? undefined : Directory.Data,
        });

        return {
            data: contents.data as string,
            filename: upload.filename || this.filenameFromPath(upload.path),
            mime_type: upload.mime_type || this.mimeFromFilename(upload.path),
        };
    }

    private filenameFromPath(path: string): string | undefined{
        const clean = path.split('?')[0];
        return clean.substring(clean.lastIndexOf('/') + 1) || undefined;
    }

    private mimeFromDataUrl(dataUrl: string): string | undefined{
        const match = dataUrl.match(/^data:([^;]+);base64,/);
        return match ? match[1] : undefined;
    }

    private mimeFromFilename(path: string): string | undefined{
        const clean = path.split('?')[0].toLowerCase();

        if(clean.endsWith('.png')){
            return 'image/png';
        }

        if(clean.endsWith('.jpg') || clean.endsWith('.jpeg')){
            return 'image/jpeg';
        }

        if(clean.endsWith('.webp')){
            return 'image/webp';
        }

        return undefined;
    }

    private errorMessage(error: any): string{
        if(error?.error?.message){
            return error.error.message;
        }

        if(error?.message){
            return error.message;
        }

        try{
            return JSON.stringify(error);
        }catch(_){
            return 'Erro ao enviar upload';
        }
    }

    private async deleteUpload(uuid: string): Promise<void>{
        await firstValueFrom(
            this.http.delete(environment.url + 'uploads/' + uuid, {
                headers: {showLoader: 'false', showError: 'false'}
            })
        ).catch(() => null);
    }
}
